import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling"); // polling | success | failed
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    const session_id = params.get("session_id");
    if (!session_id) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          await checkAuth();
          setTimeout(() => navigate("/welcome", { replace: true }), 900);
          return;
        }
      } catch (_e) { /* poll retry */ }
      attempts++;
      if (attempts >= 20) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
  }, [params, navigate, checkAuth]);

  return (
    <div className="min-h-screen grid-bg flex flex-col">
      <header className="px-8 py-6">
        <Logo size="md" />
      </header>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="glass rounded-3xl p-12 max-w-md text-center" data-testid="payment-success-panel">
          {status === "polling" && (
            <>
              <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white tracking-tight">
                Confirming your subscription…
              </h2>
              <p className="text-slate-400 mt-3 text-sm">
                Waiting on Stripe to finalize the transaction.
              </p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white tracking-tight">
                Subscription activated
              </h2>
              <p className="text-slate-300 mt-3">
                Redirecting to your Receipt Sheets welcome…
              </p>
            </>
          )}
          {status === "failed" && (
            <>
              <h2 className="text-2xl font-bold text-white tracking-tight">
                Something went wrong
              </h2>
              <p className="text-slate-400 mt-3 text-sm">
                We couldn’t confirm your payment. Try again from the pricing page.
              </p>
              <button
                onClick={() => navigate("/paywall")}
                className="mt-6 h-11 px-5 rounded-lg bg-emerald-500 text-slate-950 font-semibold"
              >
                Back to pricing
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
